import React from 'react';
import { FaClock, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

// Shared display helpers for logbook entries — used by ViewLogbooks.js, DeanDashboard.js,
// and EntryDetailsModal.js. Pure/presentational, no component state.

export const getStatusBadge = (status) => {
  const badges = {
    draft: { icon: <FaClock />, class: 'status-draft', text: 'Draft' },
    submitted: { icon: <FaClock />, class: 'status-pending', text: 'Pending Review' },
    under_review: { icon: <FaClock />, class: 'status-pending', text: 'Under Review' },
    approved: { icon: <FaCheckCircle />, class: 'status-verified', text: 'Verified' },
    needs_revision: { icon: <FaExclamationTriangle />, class: 'status-revision', text: 'Needs Revision' },
    rejected: { icon: <FaExclamationTriangle />, class: 'status-rejected', text: 'Rejected' }
  };
  return badges[status] || badges.submitted;
};

// Inline "Show Details" preview of what a student entered for a module — used by the
// expandable section of the card list in both ViewLogbooks.js and DeanDashboard.js.
// `modules` is the course's modules array (each with .moduleId/.fields); resolved per-call
// since callers already hold their own course-scoped `modules` state.
export const renderEntryData = (entry, modules) => {
  const moduleFields = modules.find(m => m.moduleId === entry.moduleId)?.fields || [];

  if (entry.entryType === 'single' && entry.entries) {
    return (
      <div className="entry-data-preview">
        <h5>Entries ({entry.entries.length})</h5>
        {entry.entries.map((e, index) => (
          <div key={e.entryId || index} className="entry-preview-card">
            <div className="entry-preview-header">
              <span className="entry-number">Entry #{index + 1}</span>
              <span className="entry-date">{formatDate(e.submittedAt)}</span>
            </div>
            <div className="entry-preview-fields">
              {moduleFields.map(field => {
                const value = e.data?.[field.key];
                return (
                  <div key={field.key} className="field-preview">
                    <span className="field-preview-label">{field.label}:</span>
                    <span className="field-preview-value">
                      {value !== undefined && value !== null ?
                        (field.type === 'date' ? formatDate(value) : value.toString())
                        : 'N/A'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  } else if (entry.entryType === 'bulk' && entry.counters) {
    return (
      <div className="counters-preview">
        <h5>Counters</h5>
        <div className="counters-preview-grid">
          {moduleFields.map(field => {
            const value = entry.counters[field.key] || 0;
            return (
              <div key={field.key} className="counter-preview">
                <div className="counter-preview-label">{field.label}</div>
                <div className="counter-preview-value">
                  {value}
                  {field.target && <span className="counter-target"> / {field.target}</span>}
                </div>
                {field.target && (
                  <div className="counter-preview-bar">
                    <div
                      className="counter-preview-fill"
                      style={{
                        width: `${Math.min((value / field.target) * 100, 100)}%`,
                        backgroundColor: value >= field.target ? '#2ECC71' : '#F39C12'
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return <p className="no-data-preview">No data available</p>;
};

// dd-mm-yyyy
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// dd-mm-yyyy HH:MM
export const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

// Renders a list of reviews (archived reviewHistory entries, or a single current review) —
// used in the verify modal ("Previous Attempts" / "Current Verification") and the details modal.
export const renderReviewList = (reviews, heading) => (
  <div className="detail-section verification-section">
    <h4>{heading}</h4>
    {reviews.map((h, i) => (
      <div key={i} className="detail-grid" style={{ marginBottom: 12 }}>
        {reviews.length > 1 && (
          <div className="detail-item">
            <span className="detail-label">Attempt:</span>
            <span className="detail-value">
              #{i + 1}{h.reason === 'reverified' ? ' — Re-verified by different faculty' : h.reason === 'reattempt' ? ' — Student reattempt' : h.reason === 'hod_reattempt' ? ' — HOD requested reattempt' : ''}
            </span>
          </div>
        )}
        <div className="detail-item">
          <span className="detail-label">Verified By:</span>
          <span className="detail-value">{h.reviewerName}</span>
        </div>
        {h.reviewedAt && (
          <div className="detail-item">
            <span className="detail-label">Verified On:</span>
            <span className="detail-value">{formatDateTime(h.reviewedAt)}</span>
          </div>
        )}
        {h.rating && (
          <div className="detail-item">
            <span className="detail-label">Rating:</span>
            <span className="detail-value">
              {h.rating === 'E' ? '⭐⭐⭐ Exceeds Expectations' : h.rating === 'M' ? '⭐⭐ Meets Expectations' : '⭐ Below Expectations'}
            </span>
          </div>
        )}
        {h.attemptActivity && (
          <div className="detail-item">
            <span className="detail-label">Attempt Activity:</span>
            <span className="detail-value">
              {h.attemptActivity === 'F' ? 'First/Only' : h.attemptActivity === 'R' ? 'Repeat' : 'Remedial'}
            </span>
          </div>
        )}
        {h.facultyDecision && (
          <div className="detail-item">
            <span className="detail-label">Faculty Decision:</span>
            <span className="detail-value">
              {h.facultyDecision === 'C' ? 'Completed' : h.facultyDecision === 'R' ? 'Repeat' : 'Remedial'}
            </span>
          </div>
        )}
        {h.feedback && (
          <div className="detail-item full-width">
            <span className="detail-label">Remarks:</span>
            <span className="detail-value">{h.feedback}</span>
          </div>
        )}
      </div>
    ))}
  </div>
);
